import * as React from "react";

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** Fetch again. Every screen with a write needs this after the write. */
  reload: () => void;
}

/**
 * Load something when the screen mounts, and again on demand.
 *
 * THE STALE ANSWER IS DROPPED. Every screen here has filters, and a filter
 * changed twice quickly starts two requests whose order of arrival is not the
 * order they were made in -- so the slower FIRST one would paint over the
 * second and the table would show the filter the operator had already left.
 * The flag is per-effect-run rather than a ref, which is what makes it correct
 * under React's double-invoked effects in development too.
 */
export function useAsync<T>(load: () => Promise<T>, deps: React.DependencyList): AsyncState<T> {
  const [data, setData] = React.useState<T | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [nonce, setNonce] = React.useState(0);

  // `load` is a fresh closure on every render, so it cannot be a dependency
  // without re-running forever. The caller's `deps` are the real dependency
  // list, which is why they are passed in rather than inferred.
  const latest = React.useRef(load);
  latest.current = load;

  React.useEffect(() => {
    let alive = true;
    setLoading(true);
    latest
      .current()
      .then((result) => {
        if (!alive) return;
        setData(result);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        // The data is NOT cleared. A reload that fails should leave the last
        // good answer on screen with the error beside it -- blanking the table
        // loses the row the operator was reading because the network blinked.
        setError(err instanceof Error ? err.message : "Could not load this");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = React.useCallback(() => setNonce((n) => n + 1), []);
  return { data, loading, error, reload };
}

/**
 * An action with a button attached: busy while it runs, error when it fails.
 *
 * Every write in this panel goes through one of these, so "did it work" is
 * answered the same way on fourteen screens instead of fourteen ways.
 *
 * `run` takes anything that returns a promise, and asks the RESULT whether it
 * carries warnings. It used to be typed `() => Promise<{ warnings?: string[] }
 * | void>`, which the screen-level wrappers satisfied by writing
 * `fn as () => Promise<void>` over a function returning something else -- a
 * cast asserting the answer has no warnings in it, at call sites where it
 * might. Asking the result instead means a write that starts returning
 * warnings is shown them rather than having them cast away.
 *
 * There is deliberately no `afterEach` option. Of the seventeen `run` call
 * sites, FIVE are the single line `if (await action.run(f)) onSaved()`; two
 * more gate a follow-up on success but do several things in it, and the other
 * ten do something different afterwards -- navigate, close a dialog, reload
 * whether it worked or not, remember whether the answer had warnings. A
 * callback here would fold five one-liners and leave twelve, while adding a
 * piece of timing for every reader of this hook to hold in their head.
 *
 * Counted twice: the first version of this paragraph said twelve and five,
 * from before the splits in this round moved the number, and the second said
 * seventeen and seven by counting "gates something on success" as "is one
 * line". An argument from a count is a claim with an expiry date.
 */
export function useAction() {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [warnings, setWarnings] = React.useState<string[] | undefined>(undefined);

  const run = React.useCallback(
    async (fn: () => Promise<unknown>): Promise<boolean> => {
      setBusy(true);
      setError(null);
      setWarnings(undefined);
      try {
        const result = await fn();
        // Warnings ride with a SUCCESS. They are not failures and must not be
        // shown as one: the row saved, and what they describe is a judgement
        // the build will refuse later or a readability problem.
        setWarnings(warningsOf(result));
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : "That did not work");
        return false;
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  const reset = React.useCallback(() => {
    setError(null);
    setWarnings(undefined);
  }, []);

  /**
   * A refusal this screen made itself, shown where a server refusal would be.
   *
   * For the case a screen can answer without asking -- a page key this build
   * does not know, say. Without it the only way to decline is `return`, and a
   * silent `return` leaves a button that is neither disabled nor responsive,
   * which is the worst of the three possible behaviours.
   */
  const fail = React.useCallback((message: string) => {
    setError(message);
    setWarnings(undefined);
  }, []);

  return { busy, error, warnings, run, reset, fail };
}

/**
 * The `warnings` of an answer, if it has any.
 *
 * Asked of an `unknown` rather than promised by a type, because the answers
 * that carry warnings and the ones that do not come back through the same
 * function. A shape check is what a cast was pretending to be.
 */
function warningsOf(result: unknown): string[] | undefined {
  if (typeof result !== "object" || result === null) return undefined;
  const { warnings } = result as { warnings?: unknown };
  if (!Array.isArray(warnings)) return undefined;
  return warnings.every((w) => typeof w === "string") ? (warnings as string[]) : undefined;
}
